import React from 'react';
import styled, { css } from 'styled-components';
import { createSiteSeller } from '../firestore';
import { validateEmail } from '../utils/email';

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
`;

const Input = styled.input<{ validate?: boolean }>`
  border: none;
  border-bottom: 2px solid rgba(255, 255, 255, 0.3);
  width: 290px;
  height: 38px;
  background: none;
  color: #ffffff;
  font-size: 16px;
  line-height: 22px;
  font-family: 'Montserrat', sans-serif;
  font-weight: bold;
  ::placeholder {
    color: #ffffff;
    opacity: 0.6;
    font-family: 'Montserrat', sans-serif;
    font-weight: normal;
  }
  :focus {
    border-bottom: 2px solid #0effca;
    outline: none;
  }
  @media (max-width: 640px) {
    width: 100%;
    display: block;
  }
  ${props =>
    props.validate &&
    css`
      border-bottom: 2px solid #9f3a38;
      color: #9f3a38;
      ::placeholder {
        color: #9f3a38;
      }
      :focus {
        border-bottom: 2px solid #9f3a38;
      }
    `}
`;

const Button = styled.button`
  font-family: 'Montserrat', sans-serif;
  font-weight: bold;
  width: 317px;
  height: 50px;
  text-align: center;
  display: inline-block;
  cursor: pointer;
  background-color: #e4ff5f;
  font-size: 14px;
  color: #a281f4;
  border-radius: 25px 25px;
  border: none;
  outline: none;
  margin: 30px 0;
  @media (max-width: 640px) {
    width: 100%;
  }
`;

const Row = styled.div`
  display: flex;
  @media (max-width: 640px) {
    display: block;
    width: 100%;
  }
`;

const Column = styled.div`
  position: relative;
  display: inline-block;
  margin: 16px 0;
  & + & {
    margin-left: 50px;
  }
  @media (max-width: 640px) {
    padding: 0 30px;
    display: block;
    margin: 8px 0;
    & + & {
      margin: 16px 0 8px 0;
    }
  }
`;

const Label = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-weight: bold;
  position: relative;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 16px 0;
  @media (max-width: 640px) {
    width: 100%;
    padding-top: 8px;
  }
`;

const Textarea = styled.textarea`
  font-family: 'Montserrat', sans-serif;
  font-weight: bold;
  display: flex;
  position: relative;
  background: rgba(255, 255, 255, 0.3);
  width: 632px;
  height: 152px;
  color: #ffffff;
  font-size: 16px;
  line-height: 22px;
  outline: none;
  border: none;
  @media (max-width: 640px) {
    width: 100%;
    display: block;
  }
`;

const Message = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.9);
  position: absolute;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  color: rgba(0, 0, 0, 0.4);
  top: 200px;
`;

const MessageContent = styled.div`
  padding: 40px 0;
`;

const MessageBtn = styled.div`
  cursor: pointer;
  float: right;
  line-height: 10px;
  :hover {
    color: rgba(0, 0, 0, 0.6);
  }
`;

interface SiteSellerState {
  isValidate: boolean;
  isSubmitting: boolean;
  message: string;
  siteSeller: {
    firstName: string;
    lastName: string;
    email: string;
    socialMediaUrl: string;
    storeUrl: string;
    phoneNumber: string;
    note: string;
  };
}

class SiteSeller extends React.Component<{}, SiteSellerState> {
  constructor(props: any) {
    super(props);

    this.state = {
      isValidate: false,
      isSubmitting: false,
      message: '',
      siteSeller: {
        firstName: '',
        lastName: '',
        email: '',
        socialMediaUrl: '',
        storeUrl: '',
        phoneNumber: '',
        note: ''
      }
    };

    this.close = this.close.bind(this);
  }

  public close() {
    setTimeout(() => {
      this.setState({ message: '' });
    }, 5000);
  }

  public onClick = async () => {
    const { firstName, lastName, email } = this.state.siteSeller;
    this.setState({
      message: `Thank you ${firstName} ${lastName}. Your message has been received.`
    });

    if (firstName && lastName && email && validateEmail(email)) {
      this.setState({ isSubmitting: true });
      try {
        await createSiteSeller(this.state.siteSeller);
        const message = `Thank you ${firstName} ${lastName}. Your message has been received.`;
        this.setState({
          isSubmitting: false,
          isValidate: false,
          message,
          siteSeller: {
            firstName: '',
            lastName: '',
            email: '',
            socialMediaUrl: '',
            storeUrl: '',
            phoneNumber: '',
            note: ''
          }
        });
        this.close();
      } catch (error) {
        console.log(error);
        this.setState({
          isSubmitting: false,
          message: 'Failed'
        });
        this.close();
      }
    } else {
      this.setState({ isValidate: true });
    }
  };
  public render() {
    const { isValidate, isSubmitting, message, siteSeller } = this.state;

    const {
      firstName,
      lastName,
      email,
      socialMediaUrl,
      storeUrl,
      phoneNumber,
      note
    } = siteSeller;

    const onChange = (event: any) => {
      const { value, name } = event.target;
      // @ts-ignore
      siteSeller[name] = value;
      // @ts-ignore
      this.setState(siteSeller);
    };

    return (
      <Container>
        <Row>
          <Column>
            <Input
              value={firstName}
              name="firstName"
              placeholder="First Name*"
              onChange={onChange}
              validate={isValidate && !firstName}
            />
          </Column>
          <Column>
            <Input
              value={lastName}
              name="lastName"
              placeholder="Last Name*"
              onChange={onChange}
              validate={isValidate && !lastName}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Input
              value={email}
              name="email"
              placeholder="E-mail*"
              onChange={onChange}
              validate={Boolean(
                (isValidate && !email) || (email && !validateEmail(email))
              )}
            />
          </Column>
          <Column>
            <Input
              value={socialMediaUrl}
              name="socialMediaUrl"
              placeholder="Social Media URL"
              onChange={onChange}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Input
              value={storeUrl}
              name="storeUrl"
              placeholder="Online Store URL"
              onChange={onChange}
            />
          </Column>
          <Column>
            <Input
              value={phoneNumber}
              name="phoneNumber"
              placeholder="Phone Number"
              onChange={onChange}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Label>Additional Notes</Label>
            <Textarea value={note} name="note" onChange={onChange} />
          </Column>
        </Row>
        <Row>
          <Column>
            <Button disabled={isSubmitting} onClick={this.onClick}>
              {isSubmitting ? 'Submitting' : 'Submit'}
            </Button>
          </Column>
        </Row>
        {message ? (
          <Message>
            <MessageBtn onClick={() => this.setState({ message: '' })}>
              X
            </MessageBtn>
            <MessageContent>{message}</MessageContent>
          </Message>
        ) : null}
      </Container>
    );
  }
}

export default SiteSeller;
